/**
 * MobileQuickActions — FAB speed-dial for mobile.
 *
 * Tap + → expands to 4 actions: Notiz, Aufgabe, Bonitur, PoI.
 * + rotates to x on open. Backdrop dims. Closes on select/backdrop/scroll.
 * One FAB per screen, constructive actions only.
 *
 * UX-P2: FAB + Bonitur fix sprint.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const ACTIONS = [
  { id: 'note', icon: '📝', labelKey: 'quickCreate.note' },
  { id: 'task', icon: '✅', labelKey: 'quickCreate.task' },
  { id: 'scouting', icon: '🔍', labelKey: 'quickCreate.scouting' },
  { id: 'poi', icon: '📍', labelKey: 'quickCreate.poi' },
];

export default function MobileQuickActions({ onAction }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Close on scroll
  useEffect(() => {
    if (!open) return;
    const handleScroll = () => setOpen(false);
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', handleScroll, { capture: true });
  }, [open]);

  const handleSelect = (actionId) => {
    setOpen(false);
    onAction?.(actionId);
  };

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-[var(--aif-z-map-overlay)] bg-black/30 transition-opacity"
          onClick={() => setOpen(false)}
          data-testid="fab-backdrop"
        />
      )}

      {/* Speed-dial + FAB */}
      <div className="fixed bottom-20 right-4 z-[var(--aif-z-map-banner)] flex flex-col items-end gap-2.5" data-testid="mobile-fab-container">
        {/* Action items (visible when open) */}
        {open && ACTIONS.map((a, i) => (
          <button
            key={a.id}
            onClick={() => handleSelect(a.id)}
            className="flex items-center gap-2.5 pl-4 pr-5 rounded-full bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-[var(--aif-text-primary)] shadow-lg min-h-[var(--aif-touch-min)]"
            style={{ animationDelay: `${i * 40}ms` }}
            data-testid={`fab-action-${a.id}`}
          >
            <span className="text-lg">{a.icon}</span>
            <span className="text-sm font-medium whitespace-nowrap">{t(a.labelKey)}</span>
          </button>
        ))}

        {/* FAB button */}
        <button
          onClick={() => setOpen(!open)}
          className="w-14 h-14 rounded-full bg-[var(--aif-color-primary)] text-white shadow-lg flex items-center justify-center transition-transform"
          style={{ transform: open ? 'rotate(45deg)' : 'none' }}
          aria-label={t('quickCreate.fabLabel', 'Schnell erstellen')}
          aria-expanded={open}
          data-testid="mobile-fab"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        </button>
      </div>
    </>
  );
}
